<div class="options-table-container">
  <div class="table-header">
    <p-iconfield iconPosition="left">
      <p-inputicon>
        <i class="pi pi-search"></i>
      </p-inputicon>
      <input type="text" pInputText placeholder="Search options..." (input)="onSearch($event)" />
    </p-iconfield>
    <p-button label="Create Option" icon="pi pi-plus" severity="primary" (onClick)="onCreateOption()"></p-button>
  </div>

  <p-table
    [scrollable]="true"
    [value]="filteredOptions()"
    [loading]="isLoading()"
    [paginator]="true"
    [rows]="10"
    [rowsPerPageOptions]="[10, 25, 50]"
    [tableStyle]="{ 'min-width': '50rem' }"
  >
    <ng-template pTemplate="header">
      <tr>
        @for (col of columns(); track col.field) {
          <th [pSortableColumn]="col.sortable ? col.field : undefined">
            {{ col.header }}
            @if (col.sortable) {
              <p-sortIcon></p-sortIcon>
            }
          </th>
        }
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-option>
      <tr>
        <td>
          <strong>{{ option.name }}</strong>
        </td>
        <td>
          <code class="code-cell">{{ option.optionCode || '—' }}</code>
        </td>
        <td>
          <span class="secondary-text">{{ option.description || 'No description' }}</span>
        </td>
        <td>
          <strong>{{ formatCurrency(option.baselinePrice) }}</strong>
        </td>
        <td>
          <div class="features-count">
            @if (
              (option.featureCount !== undefined && option.featureCount > 0) ||
              (option.featureIds && option.featureIds.length > 0)
            ) {
              @let count = option.featureCount ?? option.featureIds?.length ?? 0;
              <p-tag severity="info" [value]="count + ' feature' + (count !== 1 ? 's' : '')"></p-tag>
            } @else {
              <span class="secondary-text">No features</span>
            }
          </div>
        </td>
        <td>
          <p-tag
            [value]="getRequiredLabel(option.isRequired)"
            [severity]="getRequiredSeverity(option.isRequired)"
          ></p-tag>
        </td>
        <td>
          @if (option.isDefault) {
            <p-tag value="Default" severity="warn"></p-tag>
          } @else {
            <span class="secondary-text">-</span>
          }
        </td>
        <td>
          <p-tag [value]="getStatusLabel(option.isActive)" [severity]="getStatusSeverity(option.isActive)"></p-tag>
        </td>
        <td>
          <div class="action-buttons">
            <p-button
              icon="pi pi-pencil"
              severity="info"
              pTooltip="Edit"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              (onClick)="onEditOption(option)"
            ></p-button>
            <p-button
              icon="pi pi-trash"
              severity="danger"
              pTooltip="Delete"
              tooltipPosition="top"
              [text]="true"
              [rounded]="true"
              [loading]="deletingId() === option.optionId"
              (onClick)="onDeleteOption(option)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="columns().length">
          <div class="empty-state">
            <i class="pi pi-sliders-h large-icon"></i>
            <p>No options found</p>
            @if (searchTerm()) {
              <small>Try adjusting your search criteria</small>
            } @else {
              <small>Create your first option to get started</small>
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  [header]="dialogTitle()"
  [modal]="true"
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  [(visible)]="showDialog"
>
  <div class="dialog-content">
    @if (isEditMode() && selectedOptionCode()) {
      <div class="form-field">
        <label for="optionCode">Option Code</label>
        <input
          class="field-input"
          id="optionCode"
          type="text"
          pInputText
          readonly
          [value]="selectedOptionCode()"
        />
        <small class="help-text">Auto-generated identifier (read-only)</small>
      </div>
    }

    <div class="form-field">
      <label for="name">Option Name *</label>
      <input
        class="field-input"
        id="name"
        type="text"
        pInputText
        placeholder="Enter option name"
        [formField]="optionForm.name"
      />
      @if (optionForm.name().touched() && !optionForm.name().valid()) {
        <small class="p-error">
          @for (error of optionForm.name().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="description">Description</label>
      <textarea
        class="field-input"
        id="description"
        placeholder="Enter option description"
        rows="3"
        [formField]="optionForm.description"
      ></textarea>
    </div>

    <div class="form-field">
      <label for="categoryCode">Category Code *</label>
      <input
        class="field-input"
        id="categoryCode"
        type="text"
        pInputText
        placeholder="e.g. MKT"
        [formField]="optionForm.categoryCode"
      />
      @if (optionForm.categoryCode().touched() && !optionForm.categoryCode().valid()) {
        <small class="p-error">
          @for (error of optionForm.categoryCode().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="shorthandCode">Shorthand Code *</label>
      <input
        class="field-input"
        id="shorthandCode"
        type="text"
        pInputText
        placeholder="e.g. PRO"
        [formField]="optionForm.shorthandCode"
      />
      @if (optionForm.shorthandCode().touched() && !optionForm.shorthandCode().valid()) {
        <small class="p-error">
          @for (error of optionForm.shorthandCode().errors(); track error.kind) {
            {{ error.message }}
          }
        </small>
      }
    </div>

    <div class="form-field">
      <label for="baselinePrice">Base Price *</label>
      <p-inputNumber
        class="field-input"
        id="baselinePrice"
        name="baselinePrice"
        mode="currency"
        currency="USD"
        locale="en-US"
        [min]="0"
        [showButtons]="true"
        [ngModel]="optionForm.baselinePrice().value()"
        (ngModelChange)="optionForm.baselinePrice().value.set($event)"
      ></p-inputNumber>
    </div>

    <div class="form-field">
      <label for="featureIds">Features</label>
      <p-multiselect
        class="field-input"
        id="featureIds"
        optionLabel="name"
        optionValue="featureId"
        placeholder="Select features"
        appendTo="body"
        [ngModel]="optionFormModel().featureIds"
        [options]="features()"
        [showToggleAll]="true"
        [filter]="true"
        (ngModelChange)="onFeaturesChange($event)"
      ></p-multiselect>
    </div>

    <div class="form-field">
      <label for="productLine">Product Line</label>
      <p-select
        class="field-input"
        id="productLine"
        optionLabel="label"
        optionValue="value"
        [ngModel]="optionFormModel().productLine"
        [options]="productLineOptions()"
        (ngModelChange)="onProductLineChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="pricingFrequency">Pricing Frequency</label>
      <p-select
        class="field-input"
        id="pricingFrequency"
        optionLabel="label"
        optionValue="value"
        [ngModel]="optionFormModel().pricingFrequency"
        [options]="pricingFrequencyOptions()"
        (ngModelChange)="onPricingFrequencyChange($event)"
      ></p-select>
    </div>

    <div class="form-field">
      <label for="oneTimeFee">One-Time Fee</label>
      <p-inputNumber
        class="field-input"
        id="oneTimeFee"
        name="oneTimeFee"
        mode="currency"
        currency="USD"
        locale="en-US"
        [ngModel]="optionForm.oneTimeFee().value()"
        (ngModelChange)="optionForm.oneTimeFee().value.set($event)"
      ></p-inputNumber>
      <small class="help-text">Optional one-time charge in addition to the base price</small>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="optionForm.isRequired" />
        <span>Required (membership must include this option)</span>
      </label>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="optionForm.isDefault" />
        <span>Default Option</span>
      </label>
    </div>

    <div class="form-field checkbox-field">
      <label>
        <input type="checkbox" [formField]="optionForm.isActive" />
        <span>Active (option is shown to users)</span>
      </label>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="showDialog.set(false)"></p-button>
    <p-button
      [label]="dialogButtonLabel()"
      [loading]="saving()"
      [disabled]="!isFormValid()"
      (onClick)="onSaveOption()"
    ></p-button>
  </ng-template>
</p-dialog>
